import { supabase } from './supabase';
import type { 
  DatabaseGallery, 
  ClientGallery, 
  ClientGalleryImage 
} from './supabase';
import { generateSignedUrl, deleteFromS3, isAWSConfigured, getAWSStatus } from './serverlessS3Service';
import { uploadToS3Direct } from './directS3Upload';
import { sendPurchaseConfirmation, sendAdminPurchaseNotification } from './resendEmailService';
import { processImageForPreview, getOptimalPreviewOptions, isImageFile } from './imageProcessor';

// Simple helpers for file validation
const isValidImageFile = (file: File): { valid: boolean; error?: string } => {
  // Only accept common image types
  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/tiff'];
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: `Only JPG, PNG, WebP, and TIFF files allowed` };
  }
  
  // Max 1GB per file (plenty for photos)
  const maxSize = 1024 * 1024 * 1024; // 1GB
  if (file.size > maxSize) {
    return { valid: false, error: `File too large. Max size: 1GB` };
  }
  
  return { valid: true };
};

// Generate random gallery token
const generateToken = (): string => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

// Create a new gallery - uploads everything to AWS S3
export const createGallery = async (
  title: string,
  clientName: string,
  pricePerImage: number,
  imageFiles: File[],
  expirationDays?: number,
  clientEmail?: string
): Promise<{ success: boolean; galleryToken?: string; galleryId?: string; error?: string }> => {
  try {
    const galleryToken = generateToken();
    
    console.log(`Creating gallery with ${imageFiles.length} images`);
    
    // Check AWS configuration
    if (!isAWSConfigured()) {
      const awsStatus = getAWSStatus();
      return { 
        success: false, 
        error: `AWS S3 not configured. Missing: ${!awsStatus.hasCredentials ? 'credentials' : ''} ${!awsStatus.bucket ? 'bucket name' : ''}`.trim()
      };
    }

    // Check all files are valid images
    for (const file of imageFiles) {
      const validation = isValidImageFile(file);
      if (!validation.valid) {
        return { success: false, error: `${file.name}: ${validation.error}` };
      }
    }
    
    // Calculate expiration date if provided
    const expiresAt = expirationDays 
      ? new Date(Date.now() + expirationDays * 24 * 60 * 60 * 1000).toISOString()
      : null;

    // Create gallery record
    const { data: gallery, error: galleryError } = await supabase
      .from('client_galleries')
      .insert({
        token: galleryToken,
        title,
        client_name: clientName,
        client_email: clientEmail,
        price_per_image: pricePerImage,
        is_active: true,
        expires_at: expiresAt
      })
      .select('id')
      .single();

    if (galleryError) throw galleryError;

    // Upload each image (both preview and original to S3)
    const uploadResults = [];
    for (let i = 0; i < imageFiles.length; i++) {
      const file = imageFiles[i];
      const timestamp = Date.now();
      
      console.log(`Uploading ${file.name}...`);
      
      // Create optimized preview version
      let previewFile = file;
      if (isImageFile(file)) {
        try {
          const previewOptions = getOptimalPreviewOptions(file.size);
          previewFile = await processImageForPreview(file, previewOptions);
        } catch (error) {
          console.warn(`Preview optimization failed for ${file.name}, using original`);
        }
      }
      
      // Upload preview to S3 (small, optimized version)
      const previewFilename = `${galleryToken}_${i + 1}_${timestamp}_preview.jpg`;
      const previewUpload = await uploadToS3Direct(previewFile, previewFilename, {
        folder: 'previews',
        contentType: 'image/jpeg'
      });
      if (!previewUpload.success) {
        throw new Error(`Failed to upload preview for ${file.name}: ${previewUpload.error}`);
      }

      // Upload original to S3 (full quality version)
      const originalFilename = `${galleryToken}_${i + 1}_${timestamp}_original.${file.name.split('.').pop()}`;
      const originalUpload = await uploadToS3Direct(file, originalFilename, {
        folder: 'originals',
        contentType: file.type
      });
      if (!originalUpload.success) {
        throw new Error(`Failed to upload original for ${file.name}: ${originalUpload.error}`);
      }

      // Save image info to database
      const { error: dbError } = await supabase
        .from('gallery_images')
        .insert({
          gallery_id: gallery.id,
          filename: previewFilename,
          preview_url: previewUpload.url!,
          original_url: originalUpload.url!,
          original_storage_provider: 'aws_s3',
          original_file_size: file.size,
          original_mime_type: file.type,
          upload_status: 'completed',
          price: pricePerImage,
          position: i
        });

      if (dbError) throw dbError;

      uploadResults.push({
        filename: file.name,
        previewUrl: previewUpload.url,
        originalUrl: originalUpload.url
      });
      
      console.log(`✓ Uploaded ${file.name}`);
    }

    console.log(`✓ Gallery created with ${uploadResults.length} images`);

    return { 
      success: true, 
      galleryToken,
      galleryId: gallery.id
    };
  } catch (error) {
    console.error('Error creating gallery:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to create gallery' 
    };
  }
};

// Check if gallery has been purchased (any purchase grants access to everyone)
export const checkGalleryAccess = async (galleryToken: string): Promise<{
  hasAccess: boolean;
  purchaseId?: string;
}> => {
  try {
    const { data: galleryData } = await supabase
      .from('client_galleries')
      .select('id, price_per_image')
      .eq('token', galleryToken)
      .single();

    if (!galleryData) {
      return { hasAccess: false };
    }

    // If gallery is free (price = 0), grant access immediately
    if (galleryData.price_per_image === 0) {
      return { hasAccess: true };
    }

    // Check if ANY purchase exists for this gallery
    const { data: purchase } = await supabase
      .from('gallery_purchases')
      .select('id')
      .eq('gallery_id', galleryData.id)
      .eq('status', 'paid')
      .limit(1)
      .single();

    return {
      hasAccess: !!purchase,
      purchaseId: purchase?.id
    };
  } catch (error) {
    console.error('Error checking gallery access:', error);
    return { hasAccess: false };
  }
};

// Get gallery by token with access information
export const getGalleryByToken = async (token: string): Promise<ClientGallery | null> => {
  try {
    // Get gallery data
    const { data: gallery, error: galleryError } = await supabase
      .from('client_galleries')
      .select('*')
      .eq('token', token)
      .eq('is_active', true)
      .single();

    if (galleryError) throw galleryError;

    // Get gallery images
    const { data: images, error: imagesError } = await supabase
      .from('gallery_images')
      .select('*')
      .eq('gallery_id', gallery.id)
      .order('position');

    if (imagesError) throw imagesError;

    // Calculate expiration status
    let isExpired = false;
    let daysUntilExpiry: number | undefined;
    
    if (gallery.expires_at) {
      const now = new Date();
      const expiryDate = new Date(gallery.expires_at);
      isExpired = expiryDate < now;
      
      if (!isExpired) {
        const msUntilExpiry = expiryDate.getTime() - now.getTime();
        daysUntilExpiry = Math.ceil(msUntilExpiry / (1000 * 60 * 60 * 24));
      }
    }

    // If gallery is expired, return null (gallery not accessible)
    if (isExpired) {
      return null;
    }

    // Check if gallery has been purchased
    const accessInfo = await checkGalleryAccess(token);

    // Generate secure download URLs for images if user has access
    let processedImages: ClientGalleryImage[];
    if (accessInfo.hasAccess) {
      const imagePromises = images.map(async (img): Promise<ClientGalleryImage> => {
        let downloadUrl = img.preview_url; // Fallback to preview

        // Debug: Log the image record to see what fields we have
        console.log(`Debug image record for ${img.filename}:`, {
          upload_status: img.upload_status,
          original_storage_provider: img.original_storage_provider,
          original_url: img.original_url,
          preview_url: img.preview_url
        });

        // Generate signed URL for secure download from private S3 bucket
        if (img.original_url && img.upload_status === 'completed') {
          // Extract S3 key from URL
          if (img.original_url.includes('/originals/')) {
            const urlParts = img.original_url.split('/');
            const key = `originals/${urlParts[urlParts.length - 1]}`;
            
            console.log(`Generating signed URL for ${img.filename}, key: ${key}`);
            
            const signedUrlResult = await generateSignedUrl(key, 3600); // 1 hour expiry
            if (signedUrlResult.success && signedUrlResult.url) {
              downloadUrl = signedUrlResult.url;
              console.log(`Signed URL generated successfully for ${img.filename}`);
            } else {
              console.warn(`Failed to generate signed URL for ${img.filename}:`, signedUrlResult.error);
              downloadUrl = img.preview_url; // Fallback to preview
            }
          } else {
            downloadUrl = img.preview_url; // Fallback to preview
          }
        } else {
          // Fallback to preview if original upload failed
          downloadUrl = img.preview_url;
          console.log(`Using preview fallback for ${img.filename}: ${downloadUrl}`);
        }

        return {
          id: img.id,
          filename: img.filename,
          previewUrl: img.preview_url,
          originalUrl: downloadUrl, // This is now the proper download URL
          price: img.price
        };
      });

      processedImages = await Promise.all(imagePromises);
    } else {
      // No access - don't provide original URLs
      processedImages = images.map((img): ClientGalleryImage => ({
        id: img.id,
        filename: img.filename,
        previewUrl: img.preview_url,
        originalUrl: undefined,
        price: img.price
      }));
    }

    // Transform to client format
    const clientGallery: ClientGallery = {
      token: gallery.token,
      title: gallery.title,
      clientName: gallery.client_name,
      pricePerImage: gallery.price_per_image,
      expiresAt: gallery.expires_at,
      isExpired,
      daysUntilExpiry,
      hasAccess: accessInfo.hasAccess,
      images: processedImages
    };

    return clientGallery;
  } catch (error) {
    console.error('Error fetching gallery:', error);
    return null;
  }
};

// List all galleries (for admin)
export const listGalleries = async (): Promise<DatabaseGallery[]> => {
  try {
    const { data, error } = await supabase
      .from('client_galleries')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error listing galleries:', error);
    return [];
  }
};

// Clear all gallery data from database (preserves other data like newsletter subscribers)
export const clearAllGalleryData = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('Clearing all gallery data from database...');
    
    // Delete in correct order to respect foreign key constraints
    await supabase.from('gallery_purchases').delete().neq('id', '00000000-0000-0000-0000-000000000000');
    await supabase.from('image_purchases').delete().neq('id', '00000000-0000-0000-0000-000000000000');
    await supabase.from('gallery_images').delete().neq('id', '00000000-0000-0000-0000-000000000000');
    await supabase.from('client_galleries').delete().neq('id', '00000000-0000-0000-0000-000000000000');
    
    console.log('All gallery data cleared from database');
    return { success: true };
  } catch (error) {
    console.error('Error clearing gallery data:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to clear gallery data' 
    };
  }
};

// Delete all galleries (for cleanup/testing)
export const deleteAllGalleries = async (): Promise<{ success: boolean; deleted: number; error?: string }> => {
  try {
    console.log('Starting bulk gallery deletion...');
    
    // Get all galleries
    const galleries = await listGalleries();
    if (galleries.length === 0) {
      return { success: true, deleted: 0 };
    }
    
    console.log(`Found ${galleries.length} galleries to delete`);
    
    // Delete each gallery (this will handle S3 cleanup automatically)
    let deletedCount = 0;
    const deletePromises = galleries.map(async (gallery) => {
      const success = await deleteGallery(gallery.id);
      if (success) {
        deletedCount++;
        console.log(`Deleted gallery: ${gallery.title} (${gallery.id})`);
      } else {
        console.warn(`Failed to delete gallery: ${gallery.title} (${gallery.id})`);
      }
      return success;
    });
    
    await Promise.all(deletePromises);
    
    console.log(`Bulk deletion completed. Deleted ${deletedCount}/${galleries.length} galleries`);
    
    return { 
      success: deletedCount === galleries.length, 
      deleted: deletedCount 
    };
  } catch (error) {
    console.error('Error in bulk gallery deletion:', error);
    return { 
      success: false, 
      deleted: 0, 
      error: error instanceof Error ? error.message : 'Failed to delete galleries' 
    };
  }
};

// Delete gallery
export const deleteGallery = async (galleryId: string): Promise<boolean> => {
  try {
    // First get all images to delete from storage
    const { data: images } = await supabase
      .from('gallery_images')
      .select('filename, original_storage_provider')
      .eq('gallery_id', galleryId);

    // Delete images from AWS S3 storage
    if (images && images.length > 0) {
      console.log(`Deleting ${images.length} images from storage for gallery ${galleryId}`);
      
      const deletePromises = images.map(async (image) => {
        // Delete both preview and original from S3
        const previewKey = `previews/${image.filename}`;
        const originalKey = `originals/${image.filename.replace('_preview.jpg', '_original.')}`;
        
        try {
          // Delete preview
          await deleteFromS3(previewKey);
          
          // Delete original (extract extension from filename or assume it exists)
          const originalExtensions = ['jpg', 'jpeg', 'png', 'webp', 'tiff', 'raw'];
          for (const ext of originalExtensions) {
            const originalKeyWithExt = originalKey + ext;
            await deleteFromS3(originalKeyWithExt);
          }
        } catch (error) {
          console.warn(`Failed to delete image files for ${image.filename}:`, error);
        }
      });

      await Promise.all(deletePromises);
    }

    // Delete gallery (cascade will handle related records)
    const { error } = await supabase
      .from('client_galleries')
      .delete()
      .eq('id', galleryId);

    if (error) throw error;
    
    console.log(`Gallery ${galleryId} deleted successfully`);
    return true;
  } catch (error) {
    console.error('Error deleting gallery:', error);
    return false;
  }
};

// Generate download token (keeping for future use)
// const generateDownloadToken = (): string => {
//   return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
// };

// Purchase gallery access (simplified model)
export const purchaseGalleryAccess = async (
  galleryToken: string,
  clientEmail: string,
  paymentIntentId?: string,
  language?: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Get gallery info directly from database to avoid recursive calls
    const { data: galleryData, error: galleryError } = await supabase
      .from('client_galleries')
      .select('id, price_per_image, expires_at, is_active')
      .eq('token', galleryToken)
      .single();

    if (galleryError || !galleryData) {
      console.error('Gallery lookup error:', galleryError);
      return { success: false, error: 'Gallery not found' };
    }

    if (!galleryData.is_active) {
      return { success: false, error: 'Gallery is not active' };
    }

    // Gallery access price (use price_per_image as gallery access price)
    const accessPrice = galleryData.price_per_image;
    
    console.log('Creating gallery access purchase for:', galleryToken);
    console.log('Client email:', clientEmail);
    console.log('Access price:', accessPrice);

    // Create gallery purchase record
    const purchaseRecord = {
      gallery_id: galleryData.id,
      client_email: clientEmail,
      payment_intent_id: paymentIntentId,
      amount: accessPrice,
      status: accessPrice > 0 ? 'paid' : 'free',
      expires_at: galleryData.expires_at // Inherit gallery expiration
    };

    console.log('Purchase record to insert:', purchaseRecord);

    const { data: insertResult, error: insertError } = await supabase
      .from('gallery_purchases')
      .insert(purchaseRecord);

    console.log('Insert result:', insertResult);
    console.log('Insert error:', insertError);

    if (insertError) throw insertError;

    // Send purchase confirmation email if it's a paid gallery
    if (accessPrice > 0 && clientEmail && !clientEmail.includes('payment@gallery.purchase')) {
      try {
        // Get gallery title and image count
        const { data: galleryInfo } = await supabase
          .from('client_galleries')
          .select('title, client_name')
          .eq('id', galleryData.id)
          .single();

        const { data: imageCount } = await supabase
          .from('gallery_images')
          .select('id', { count: 'exact' })
          .eq('gallery_id', galleryData.id);

        if (galleryInfo) {
          console.log('Sending purchase confirmation email to:', clientEmail);
          const emailResult = await sendPurchaseConfirmation({
            clientEmail,
            clientName: galleryInfo.client_name || 'Valued Client',
            galleryTitle: galleryInfo.title,
            galleryToken: galleryToken,
            amount: accessPrice,
            imageCount: imageCount?.length || 0,
            paymentMethod: paymentIntentId ? 'PayPal' : 'Free Access',
            transactionId: paymentIntentId,
            language: language || 'en'
          });

          if (emailResult.success) {
            console.log('Purchase confirmation email sent successfully');
          } else {
            console.warn('Failed to send purchase confirmation email:', emailResult.error);
          }

          // Send admin notification email
          console.log('Sending admin purchase notification');
          const adminEmailResult = await sendAdminPurchaseNotification({
            clientEmail,
            clientName: galleryInfo.client_name || 'Valued Client',
            galleryTitle: galleryInfo.title,
            galleryToken: galleryToken,
            amount: accessPrice,
            imageCount: imageCount?.length || 0,
            paymentMethod: paymentIntentId ? 'PayPal' : 'Free Access',
            transactionId: paymentIntentId,
            language: language || 'en'
          });

          if (adminEmailResult.success) {
            console.log('Admin purchase notification sent successfully');
          } else {
            console.warn('Failed to send admin purchase notification:', adminEmailResult.error);
          }
        }
      } catch (emailError) {
        console.error('Error sending purchase confirmation email:', emailError);
        // Don't fail the purchase if email fails
      }
    }

    return { success: true };
  } catch (error) {
    console.error('Error processing gallery access purchase:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to process purchase' 
    };
  }
};

// Get gallery purchases for admin purposes
export const getGalleryPurchases = async (galleryId: string): Promise<{
  success: boolean;
  purchases?: Array<{
    id: string;
    clientEmail: string;
    status: string;
    amount: number;
    purchaseDate: string;
  }>;
  error?: string;
}> => {
  try {
    const { data: purchases, error } = await supabase
      .from('gallery_purchases')
      .select('id, client_email, status, amount, purchased_at')
      .eq('gallery_id', galleryId)
      .order('purchased_at', { ascending: false });

    if (error) throw error;

    if (!purchases || purchases.length === 0) {
      return { success: true, purchases: [] };
    }

    return {
      success: true,
      purchases: purchases.map(purchase => ({
        id: purchase.id,
        clientEmail: purchase.client_email,
        status: purchase.status,
        amount: purchase.amount,
        purchaseDate: purchase.purchased_at
      }))
    };
  } catch (error) {
    console.error('Error fetching gallery purchases:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to fetch purchases' 
    };
  }
};

// Get gallery by ID for editing
export const getGalleryById = async (galleryId: string): Promise<{
  success: boolean;
  gallery?: DatabaseGallery & { images: any[] };
  error?: string;
}> => {
  try {
    const { data: gallery, error: galleryError } = await supabase
      .from('client_galleries')
      .select('*')
      .eq('id', galleryId)
      .single();

    if (galleryError) throw galleryError;

    const { data: images, error: imagesError } = await supabase
      .from('gallery_images')
      .select('*')
      .eq('gallery_id', galleryId)
      .order('position');

    if (imagesError) throw imagesError;

    return {
      success: true,
      gallery: { ...gallery, images: images || [] }
    };
  } catch (error) {
    console.error('Error fetching gallery by ID:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch gallery'
    };
  }
};

// Add images to existing gallery
export const addImagesToGallery = async (
  galleryId: string,
  imageFiles: File[]
): Promise<{ success: boolean; error?: string }> => {
  try {
    
    // Get gallery info
    const { data: gallery, error: galleryError } = await supabase
      .from('client_galleries')
      .select('token, price_per_image')
      .eq('id', galleryId)
      .single();

    if (galleryError) throw galleryError;

    // Get current highest position
    const { data: existingImages } = await supabase
      .from('gallery_images')
      .select('position')
      .eq('gallery_id', galleryId)
      .order('position', { ascending: false })
      .limit(1);

    const startPosition = existingImages && existingImages.length > 0 
      ? existingImages[0].position + 1 
      : 0;

    // Check all files are valid images
    for (const file of imageFiles) {
      const validation = isValidImageFile(file);
      if (!validation.valid) {
        return { success: false, error: `${file.name}: ${validation.error}` };
      }
    }

    // Upload each new image to the gallery
    for (let i = 0; i < imageFiles.length; i++) {
      const file = imageFiles[i];
      const timestamp = Date.now();
      
      console.log(`Adding ${file.name} to gallery...`);
      
      // Create optimized preview
      let previewFile = file;
      if (isImageFile(file)) {
        try {
          const previewOptions = getOptimalPreviewOptions(file.size);
          previewFile = await processImageForPreview(file, previewOptions);
        } catch (error) {
          console.warn(`Preview optimization failed for ${file.name}`);
        }
      }
      
      // Upload preview to S3
      const previewFilename = `${gallery.token}_${timestamp}_${i}_preview.jpg`;
      const previewUpload = await uploadToS3Direct(previewFile, previewFilename, {
        folder: 'previews',
        contentType: 'image/jpeg'
      });
      if (!previewUpload.success) {
        throw new Error(`Failed to upload preview for ${file.name}`);
      }

      // Upload original to S3  
      const originalFilename = `${gallery.token}_${timestamp}_${i}_original.${file.name.split('.').pop()}`;
      const originalUpload = await uploadToS3Direct(file, originalFilename, {
        folder: 'originals',
        contentType: file.type
      });
      if (!originalUpload.success) {
        throw new Error(`Failed to upload original for ${file.name}`);
      }

      // Save to database
      const { error: dbError } = await supabase
        .from('gallery_images')
        .insert({
          gallery_id: galleryId,
          filename: previewFilename,
          preview_url: previewUpload.url!,
          original_url: originalUpload.url!,
          original_storage_provider: 'aws_s3',
          original_file_size: file.size,
          original_mime_type: file.type,
          upload_status: 'completed',
          price: gallery.price_per_image,
          position: startPosition + i
        });

      if (dbError) throw dbError;
      console.log(`✓ Added ${file.name}`);
    }

    return { 
      success: true
    };
  } catch (error) {
    console.error('Error adding images to gallery:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to add images' 
    };
  }
};

// Remove image from gallery
export const removeImageFromGallery = async (imageId: string): Promise<boolean> => {
  try {
    // Get image info first
    const { data: image, error: imageError } = await supabase
      .from('gallery_images')
      .select('filename, original_storage_provider')
      .eq('id', imageId)
      .single();

    if (imageError) throw imageError;

    // Delete from S3 storage
    if (image) {
      const previewKey = `previews/${image.filename}`;
      const originalKey = `originals/${image.filename.replace('_preview.jpg', '_original.')}`;
      
      try {
        // Delete preview
        await deleteFromS3(previewKey);
        
        // Delete original (try common extensions)
        const originalExtensions = ['jpg', 'jpeg', 'png', 'webp', 'tiff', 'raw'];
        for (const ext of originalExtensions) {
          const originalKeyWithExt = originalKey + ext;
          await deleteFromS3(originalKeyWithExt);
        }
      } catch (error) {
        console.warn(`Failed to delete S3 files for image ${imageId}:`, error);
      }
    }

    // Delete from database
    const { error } = await supabase
      .from('gallery_images')
      .delete()
      .eq('id', imageId);

    if (error) throw error;
    
    console.log(`Image ${imageId} removed successfully`);
    return true;
  } catch (error) {
    console.error('Error removing image:', error);
    return false;
  }
};

// Get purchased images by download token with secure AWS URLs
export const getPurchasedImages = async (downloadToken: string): Promise<{
  success: boolean;
  images?: Array<{ id: string; filename: string; originalUrl?: string; previewUrl: string; downloadUrl?: string }>;
  galleryTitle?: string;
  galleryToken?: string;
  isExpired?: boolean;
  daysUntilExpiry?: number;
  expiresAt?: string;
  error?: string;
}> => {
  try {
    console.log('Fetching images for download token:', downloadToken);

    // Get purchase info with gallery and image details
    const { data: purchases, error: purchaseError } = await supabase
      .from('image_purchases')
      .select(`
        *,
        gallery_images!inner(id, filename, preview_url, original_url, original_storage_provider),
        client_galleries!inner(title, token, expires_at)
      `)
      .eq('download_token', downloadToken);

    console.log('Raw purchases data:', purchases);
    console.log('Purchase error:', purchaseError);

    if (purchaseError) throw purchaseError;

    if (!purchases || purchases.length === 0) {
      return { success: false, error: 'Download token not found or expired' };
    }

    // Check gallery expiration status
    const gallery = purchases[0].client_galleries;
    let isExpired = false;
    let daysUntilExpiry: number | undefined;
    
    if (gallery.expires_at) {
      const now = new Date();
      const expiryDate = new Date(gallery.expires_at);
      isExpired = expiryDate < now;
      
      if (!isExpired) {
        const msUntilExpiry = expiryDate.getTime() - now.getTime();
        daysUntilExpiry = Math.ceil(msUntilExpiry / (1000 * 60 * 60 * 24));
      }
    }

    // If gallery is expired, return expiration info without images
    if (isExpired) {
      return { 
        success: false, 
        isExpired: true,
        galleryTitle: gallery.title,
        galleryToken: gallery.token,
        expiresAt: gallery.expires_at,
        error: 'Gallery has expired' 
      };
    }

    // Transform data and generate secure download URLs
    const imagePromises = purchases.map(async purchase => {
      const image = purchase.gallery_images;
      let downloadUrl = image.preview_url; // Fallback to preview

      // Generate signed URL for secure download from private S3 bucket
      if (image.original_storage_provider === 'aws_s3' && image.original_url && image.upload_status === 'completed') {
        // Check if the original_url contains the S3 key information
        if (image.original_url.includes('/originals/')) {
          // Extract S3 key from URL
          const urlParts = image.original_url.split('/');
          const key = `originals/${urlParts[urlParts.length - 1]}`;
          
          console.log(`Generating signed URL for download token, file: ${image.filename}, key: ${key}`);
          
          const signedUrlResult = await generateSignedUrl(key, 3600); // 1 hour expiry
          if (signedUrlResult.success && signedUrlResult.url) {
            downloadUrl = signedUrlResult.url;
            console.log(`Signed URL generated successfully for download: ${image.filename}`);
          } else {
            console.warn(`Failed to generate signed URL for ${image.filename}:`, signedUrlResult.error);
            downloadUrl = image.preview_url; // Fallback to preview
          }
        } else {
          downloadUrl = image.preview_url; // Fallback to preview
        }
      } else {
        // For failed uploads or non-AWS storage, use preview
        downloadUrl = image.preview_url;
        console.log(`Using preview fallback for download: ${image.filename}: ${downloadUrl}`);
      }

      return {
        id: image.id,
        filename: image.filename,
        originalUrl: image.original_url,
        previewUrl: image.preview_url,
        downloadUrl
      };
    });

    const images = await Promise.all(imagePromises);

    console.log('Transformed images with download URLs:', images);

    return {
      success: true,
      images,
      galleryTitle: purchases[0].client_galleries.title,
      galleryToken: purchases[0].client_galleries.token,
      isExpired,
      daysUntilExpiry,
      expiresAt: gallery.expires_at
    };
  } catch (error) {
    console.error('Error fetching purchased images:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to fetch images' 
    };
  }
};